import { createApi } from "@reduxjs/toolkit/query/react";
import { createRetryingBaseQuery } from "../retryingBaseQuery";

const baseQuery = createRetryingBaseQuery(process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? "");

export const userAPI = createApi({
  reducerPath: "userAPI",
  baseQuery,
  keepUnusedDataFor: 0,
  tagTypes: ["tagGetUsers", "tagGetCurrentLoggedUser"],
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    //* *********************************************************** */
    //* GET ALL USERS */
    getUsers: builder.query({
      query: (body: any) => ({
        url: `/users/?sort=first_name&sort_direction=asc&page=${body.page}&limit=${body.limit}${
          body.searchAndFilterString || ""
        }`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetUsers"],
    }),
    //* *********************************************************** */
    //* GET USERS BY ORGANIZATION */
    getUsersByOrganization: builder.query({
      query: (body: any) => ({
        url: `/users/?page=${body.page}&limit=${body.limit}&filter_target=organization_id&filter=${body.orgId}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetUsers"],
    }),
    //* *********************************************************** */
    //* GET USER BY ID */
    getUser: builder.query({
      query: (body: any) => ({
        url: `/users/${body.id}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetUsers"],
    }),
    //* *********************************************************** */
    //* GET THE USER IN THE CURRENT SESSION */
    getCurrentLoggedUser: builder.query({
      query: (body: any) => ({
        url: `/users/me`,
        headers: {
          authorization: body.token,
        },
      }),
      providesTags: ["tagGetCurrentLoggedUser"],
    }),
    //* *********************************************************** */
    //* POST TERMS AND CONDITIONS */
    postTermnsAndConditions: builder.mutation({
      query: (body: any) => ({
        url: "/users/terms",
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetCurrentLoggedUser"],
    }),
    //* *********************************************************** */
    //* LOGOUT */
    // Ends every session for the caller server-side. Without this, signing out
    // only cleared the browser: the refresh token stayed valid for its full
    // 30-day TTL (LIFT-51).
    postLogout: builder.mutation({
      query: (body: any) => ({
        url: "/users/logout",
        method: "POST",
        headers: { authorization: body.token },
      }),
    }),
    //* *********************************************************** */
    //* CREATE USER */
    postUser: builder.mutation({
      query: (body: any) => ({
        url: "/users/",
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "CreateUser" },
      invalidatesTags: ["tagGetUsers"],
    }),
    //* *********************************************************** */
    //* EDIT USER */
    putUser: builder.mutation({
      query: (body: any) => ({
        url: `/users/${body.id}`,
        method: "PUT",
        // Spread first, id last — guarantees the path-level id wins even if
        // a caller accidentally includes a stale/empty id inside body.body.
        // The UpdateUser FlatBuffer serializer requires id to pack UserT.
        body: { ...body.body, id: body.id },
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "UpdateUser" },
      invalidatesTags: ["tagGetUsers"],
    }),
    //* *********************************************************** */
    //* DELETE USER */
    deleteUser: builder.mutation({
      query: (body: any) => ({
        url: `/users/${body.id}`,
        method: "DELETE",
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetUsers"],
    }),
    //* *********************************************************** */
    //* RESET COGNITO USER PASSWORD */
    resetUserPassword: builder.mutation({
      query: (body: any) => ({
        url: `/users/reset/${body.id}`,
        method: "PUT",
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetUsers"],
    }),
    //* *********************************************************** */
    //* BLOCK USER */
    blockUser: builder.mutation({
      query: (body: any) => ({
        url: `/users/block/${body.id}`,
        method: "PUT",
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetUsers"],
    }),
    //* *********************************************************** */
    //* UN BLOCK USER */
    unBlockUser: builder.mutation({
      query: (body: any) => ({
        url: `/users/unblock/${body.id}`,
        method: "PUT",
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetUsers"],
    }),
    //* *********************************************************** */
    //* UPDATE OWN ACTION FIRING MODE */
    // Its own endpoint rather than a field on putUser, because the server
    // grants the two separately: changing your OWN firing mode and changing
    // SOMEONE ELSE'S are different permissions, and Authorize tells them apart
    // by the request path. Folding either into the general user update would
    // hand both to anyone who can edit a user at all.
    patchOwnActionFiringMode: builder.mutation({
      query: (body: any) => ({
        url: `/users/action-firing-mode`,
        method: "PATCH",
        headers: { authorization: body.token },
        body: { action_firing_mode: body.actionFiringMode },
      }),
      invalidatesTags: ["tagGetUsers"],
    }),
    //* *********************************************************** */
    //* UPDATE ANOTHER USER'S ACTION FIRING MODE */
    patchUserActionFiringMode: builder.mutation({
      query: (body: any) => ({
        url: `/users/action-firing-mode/${body.id}`,
        method: "PATCH",
        headers: { authorization: body.token },
        body: { action_firing_mode: body.actionFiringMode },
      }),
      invalidatesTags: ["tagGetUsers"],
    }),
    //* *********************************************************** */
  }),
});

export const {
  useLazyGetUsersQuery,
  useLazyGetUsersByOrganizationQuery,
  useLazyGetUserQuery,
  useLazyGetCurrentLoggedUserQuery,
  usePostLogoutMutation,
  usePostUserMutation,
  usePostTermnsAndConditionsMutation,
  usePutUserMutation,
  useDeleteUserMutation,
  useResetUserPasswordMutation,
  useBlockUserMutation,
  useUnBlockUserMutation,
  usePatchOwnActionFiringModeMutation,
  usePatchUserActionFiringModeMutation,
} = userAPI;
